import React, { useCallback, useEffect, useRef, useState } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { useToast, useConfirm } from '../../context/ToastContext';
import {
  listSetupTemplates,
  getSetupTemplate,
  createTemplate,
  updateTemplate,
  createSection,
  updateSection,
  deleteSection,
  getSetupCohort,
  withdrawStudent,
  reinstateStudent,
  getTemplateOverview,
  saveTemplateOverview,
  uploadTemplateOverviewDoc,
  getTemplateOverviewFileUrl,
} from '../../services/competitionSetupService';
import CompetitionOverviewEditor from '../../components/common/CompetitionOverviewEditor/CompetitionOverviewEditor';
import styles from './CompetitionSetup.module.css';

/**
 * Competition Setup — build and edit competitions and their sections, and
 * manage who is on them.
 *
 * Shared by super admins, managers and POC owners: the same page mounted under
 * both route trees rather than duplicated, so a change to how a section is
 * edited cannot drift between the two.
 *
 * Deleting is deliberately awkward where it destroys work. A section students
 * have submitted against cannot be deleted at all (the API refuses — the
 * submissions cascade from it), and removing a student is a withdrawal that
 * keeps their drafts and feedback rather than a delete that does not.
 *
 * The Details tab holds the student-facing competition overview — uploaded as a
 * guide and parsed, or typed in by hand. It has a wider blast radius than
 * anything else here: a save is live for every enrolled student at once, with
 * no draft state, which is why replacing existing details confirms first.
 */

const SUBMISSION_TYPES = [
  { value: 'group', label: 'Group (container, no submission)' },
  { value: 'rich_text', label: 'Written text' },
  { value: 'file', label: 'File upload' },
  { value: 'link', label: 'External link' },
  { value: 'file_or_link', label: 'File or link' },
  { value: 'select', label: 'Dropdown (pick one)' },
];

const CompetitionSetup = () => {
  const toast = useToast();
  const confirm = useConfirm();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [cohort, setCohort] = useState([]);
  const [tab, setTab] = useState('sections');
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      setTemplates(await listSetupTemplates());
    } catch (error) {
      toast.error('Could not load competitions: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // `silent` keeps the tree mounted while refreshing after an edit — the same
  // reason the review panel needs it: a spinner swap unmounts everything and
  // throws away anything half-typed elsewhere on the page.
  const loadDetail = useCallback(async (templateId, { silent = false } = {}) => {
    if (!templateId) return;
    if (!silent) setDetail(null);
    try {
      const [t, c] = await Promise.all([
        getSetupTemplate(templateId),
        getSetupCohort(templateId),
      ]);
      setDetail(t);
      setCohort(c || []);
    } catch (error) {
      toast.error('Could not load that competition: ' + error.message);
    }
  }, [toast]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const saveSection = async (values) => {
    setBusy(true);
    try {
      if (values.id) {
        await updateSection(values.id, values);
        toast.success('Section updated.');
      } else {
        await createSection(selectedId, values);
        toast.success('Section added.');
      }
      setEditingSection(null);
      await loadDetail(selectedId, { silent: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const removeSection = async (section) => {
    const ok = await confirm(
      `Delete “${section.title}”? This cannot be undone. If any student has ` +
        'already submitted against this section the deletion will be refused, ' +
        'to protect their work.',
      { confirmText: 'Delete section', variant: 'danger' }
    );
    if (!ok) return;
    try {
      await deleteSection(section.id);
      toast.success('Section deleted.');
      await loadDetail(selectedId, { silent: true });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const removeStudent = async (row) => {
    const ok = await confirm(
      `Remove ${row.student_name || 'this student'} from this competition? It ` +
        'disappears from their workspace, but their drafts, uploads and ' +
        'feedback are kept — you can add them back later.',
      { confirmText: 'Remove', variant: 'danger' }
    );
    if (!ok) return;
    try {
      await withdrawStudent(row.enrollment_id);
      toast.success('Removed. Their work has been kept.');
      await loadDetail(selectedId, { silent: true });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const putBack = async (row) => {
    try {
      await reinstateStudent(row.enrollment_id);
      toast.success('Added back to the competition.');
      await loadDetail(selectedId, { silent: true });
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner size="large" message="Loading competitions..." /></div>;
  }

  const sections = flatten(detail?.requirements);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Competition Setup</h1>
          <p className={styles.subtitle}>
            Create competitions, define what students submit, and manage who is on them.
          </p>
        </div>
        <Button size="small" onClick={() => setShowNewTemplate(true)}>New competition</Button>
      </div>

      <div className={styles.layout}>
        <Card>
          <div className={styles.listTitle}>Competitions</div>
          {templates.length === 0 ? (
            <p className={styles.empty}>None yet.</p>
          ) : (
            templates.map((t) => (
              <button
                key={t.id}
                className={`${styles.templateRow} ${selectedId === t.id ? styles.templateActive : ''}`}
                onClick={() => setSelectedId(t.id)}
              >
                <span className={styles.templateName}>{t.name}</span>
                {!t.active && <span className={styles.inactive}>inactive</span>}
              </button>
            ))
          )}
        </Card>

        <div className={styles.detail}>
          {!selectedId ? (
            <Card><p className={styles.empty}>Pick a competition to edit it.</p></Card>
          ) : !detail ? (
            <Card><LoadingSpinner size="small" message="Loading..." /></Card>
          ) : (
            <Card>
              <div className={styles.detailHead}>
                <div>
                  <h2 className={styles.detailTitle}>{detail.name}</h2>
                  <button className={styles.linkBtn} onClick={() => setEditingTemplate(detail)}>
                    Edit competition details
                  </button>
                </div>
                <div className={styles.tabs}>
                  <button
                    className={tab === 'sections' ? styles.tabActive : styles.tab}
                    onClick={() => setTab('sections')}
                  >
                    Sections ({sections.length})
                  </button>
                  <button
                    className={tab === 'students' ? styles.tabActive : styles.tab}
                    onClick={() => setTab('students')}
                  >
                    Students ({cohort.length})
                  </button>
                  <button
                    className={tab === 'details' ? styles.tabActive : styles.tab}
                    onClick={() => setTab('details')}
                  >
                    Details
                  </button>
                </div>
              </div>

              {tab === 'details' ? (
                <OverviewPanel templateId={selectedId} templateName={detail.name} />
              ) : tab === 'sections' ? (
                <>
                  <div className={styles.sectionActions}>
                    <Button size="small" variant="outline" onClick={() => setEditingSection({})}>
                      Add section
                    </Button>
                  </div>
                  {sections.length === 0 ? (
                    <p className={styles.empty}>No sections yet — add the first one.</p>
                  ) : (
                    sections.map((section) => (
                      <div
                        key={section.id}
                        className={styles.section}
                        style={{ paddingLeft: 12 + section.depth * 22 }}
                      >
                        <div>
                          <span className={styles.sectionTitle}>{section.title}</span>
                          <span className={styles.sectionMeta}>
                            {section.submission_type}
                            {section.required === false ? ' · optional' : ''}
                            {section.min_words || section.max_words
                              ? ` · ${section.min_words || 0}–${section.max_words || '∞'} words`
                              : ''}
                          </span>
                        </div>
                        <div className={styles.sectionButtons}>
                          <button className={styles.linkBtn} onClick={() => setEditingSection(section)}>
                            Edit
                          </button>
                          <button className={styles.dangerBtn} onClick={() => removeSection(section)}>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </>
              ) : cohort.length === 0 ? (
                <p className={styles.empty}>Nobody is on this competition yet.</p>
              ) : (
                cohort.map((row) => {
                  const withdrawn = row.status === 'withdrawn';
                  return (
                    <div key={row.enrollment_id} className={styles.section}>
                      <div>
                        <span className={styles.sectionTitle}>
                          {row.student_name || row.student_email}
                        </span>
                        <span className={styles.sectionMeta}>
                          {withdrawn ? 'removed from this competition' : row.overall || 'in progress'}
                        </span>
                      </div>
                      <div className={styles.sectionButtons}>
                        {withdrawn ? (
                          <button className={styles.linkBtn} onClick={() => putBack(row)}>
                            Add back
                          </button>
                        ) : (
                          <button className={styles.dangerBtn} onClick={() => removeStudent(row)}>
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </Card>
          )}
        </div>
      </div>

      {showNewTemplate && (
        <TemplateModal
          onClose={() => setShowNewTemplate(false)}
          onSave={async (values) => {
            try {
              const created = await createTemplate(values);
              toast.success('Competition created.');
              setShowNewTemplate(false);
              await loadTemplates();
              setSelectedId(created.id);
            } catch (error) {
              toast.error(error.message);
            }
          }}
        />
      )}

      {editingTemplate && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSave={async (values) => {
            try {
              await updateTemplate(editingTemplate.id, values);
              toast.success('Competition updated.');
              setEditingTemplate(null);
              await Promise.all([loadTemplates(), loadDetail(selectedId, { silent: true })]);
            } catch (error) {
              toast.error(error.message);
            }
          }}
        />
      )}

      {editingSection && (
        <SectionModal
          section={editingSection}
          groups={sections.filter((s) => s.submission_type === 'group')}
          busy={busy}
          onClose={() => setEditingSection(null)}
          onSave={saveSection}
        />
      )}
    </div>
  );
};

/** Flatten the nested requirement tree, keeping depth for indentation. */
const flatten = (nodes, depth = 0) => {
  const out = [];
  (nodes || []).forEach((n) => {
    out.push({ ...n, depth });
    if (n.children?.length) out.push(...flatten(n.children, depth + 1));
  });
  return out;
};

const OVERVIEW_EXTENSIONS = ['.docx', '.pdf'];
const OVERVIEW_MAX_MB = 10; // matches MAX_FILE_BYTES in the extraction service

/** What a published overview actually contains, for the admin to check. */
const describeOverview = (data) => {
  const stages = data?.stages || [];
  return [
    { label: 'Stages', value: stages.length },
    { label: 'With deadlines', value: stages.filter((s) => s.deadline?.date).length },
    { label: 'Deliverables', value: stages.reduce((n, s) => n + (s.deliverables?.length || 0), 0) },
    { label: 'Rules', value: (data?.rules || []).length },
    { label: 'Links', value: (data?.links || []).length },
  ];
};

/**
 * Details tab — the competition overview students read at
 * /student/competitions/:templateId.
 *
 * Two ways in, and they meet in the same editor:
 *  - Upload the competition's guide: it is stored, parsed by Claude into the
 *    overview's fields, published, and then opened in the editor so whoever
 *    uploaded it can correct the parse while the document is still to hand.
 *  - Edit directly, including building the details from nothing for a
 *    competition that has no guide to upload.
 *
 * Everything is live for students as soon as it is saved — there is no draft
 * state — which is why replacing existing details asks first.
 */
const OverviewPanel = ({ templateId, templateName }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const [meta, setMeta] = useState(null); // null until loaded
  const [uploading, setUploading] = useState(false);
  // The overview being edited, or null when the editor is closed. Set straight
  // from the upload response so a parse can be reviewed without a round trip.
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setMeta(await getTemplateOverview(templateId));
    } catch (error) {
      toast.error('Could not load the published details: ' + error.message);
      setMeta({});
    }
  }, [templateId, toast]);

  useEffect(() => { load(); }, [load]);

  const published = meta?.overview_data || null;

  const upload = async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!OVERVIEW_EXTENSIONS.some((ext) => name.endsWith(ext))) {
      toast.error('Only .docx and .pdf files are supported.');
      return;
    }
    if (file.size > OVERVIEW_MAX_MB * 1024 * 1024) {
      toast.error(`That file is too large (max ${OVERVIEW_MAX_MB} MB).`);
      return;
    }

    if (published) {
      const ok = await confirm(
        `Replace the published details for “${templateName}”? Every student on this ` +
          'competition sees the new stages, deadlines and rules immediately — there ' +
          'is no review step in between.',
        { confirmText: 'Parse and publish', variant: 'danger' }
      );
      if (!ok) return;
    }

    setUploading(true);
    try {
      const result = await uploadTemplateOverviewDoc(templateId, file);
      toast.success('Details published — check what was read below.');
      await load();
      setEditing(result.overview);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const save = async (overview) => {
    setSaving(true);
    try {
      await saveTemplateOverview(templateId, overview);
      toast.success('Competition details saved.');
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const openSource = async () => {
    try {
      window.open(await getTemplateOverviewFileUrl(templateId), '_blank', 'noopener');
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (meta === null) {
    return <LoadingSpinner size="small" message="Loading published details..." />;
  }

  if (editing) {
    return (
      <CompetitionOverviewEditor
        initial={editing}
        busy={saving}
        onSave={save}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className={styles.overviewPanel}>
      <p className={styles.overviewIntro}>
        What students see on this competition's page: its stages, deadlines,
        deliverables, rules and links. Upload the official guide to have them read
        out of the document — anything it does not state is left out rather than
        guessed — then edit anything that needs correcting or adding.
      </p>

      {published ? (
        <div className={styles.overviewStatus}>
          <div>
            <span className={styles.overviewLive}>Live for students</span>
            <span className={styles.sectionMeta}>
              from {meta.overview_source_filename || 'an uploaded document'}
              {meta.overview_parsed_at
                ? ` · ${new Date(meta.overview_parsed_at).toLocaleString()}`
                : ''}
            </span>
          </div>
          <div className={styles.sectionButtons}>
            <button className={styles.linkBtn} onClick={() => setEditing(published)}>
              Edit details
            </button>
            {meta.overview_source_path && (
              <button className={styles.linkBtn} onClick={openSource}>
                View the document
              </button>
            )}
          </div>
        </div>
      ) : (
        <p className={styles.empty}>
          Nothing published yet — students see a “details coming soon” notice on this
          competition's page.
        </p>
      )}

      {published && (
        <div className={styles.overviewCounts}>
          {describeOverview(published).map((item) => (
            <div key={item.label} className={styles.overviewCount}>
              <span className={styles.overviewCountValue}>{item.value}</span>
              <span className={styles.overviewCountLabel}>{item.label}</span>
            </div>
          ))}
        </div>
      )}

      {uploading ? (
        <div className={styles.overviewBusy}>
          <LoadingSpinner size="small" />
          <span>Reading the document and publishing — this can take up to a minute.</span>
        </div>
      ) : (
        <div className={styles.sectionActions}>
          <input
            ref={inputRef}
            type="file"
            accept=".docx,.pdf"
            className={styles.hiddenInput}
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <div className={styles.overviewButtons}>
            <Button size="small" variant="outline" onClick={() => inputRef.current?.click()}>
              {published ? 'Upload a new guide' : 'Upload competition guide'}
            </Button>
            {/* An empty object opens the editor with every section blank. */}
            {!published && (
              <Button size="small" variant="outline" onClick={() => setEditing({})}>
                Add details by hand
              </Button>
            )}
          </div>
          <span className={styles.fieldHint}>
            Word (.docx) or PDF, up to {OVERVIEW_MAX_MB} MB. No guide to upload? Add the
            details by hand instead.
          </span>
        </div>
      )}
    </div>
  );
};

const TemplateModal = ({ template, onClose, onSave }) => {
  const editing = Boolean(template);
  const [values, setValues] = useState({
    slug: template?.slug || '',
    name: template?.name || '',
    description: template?.description || '',
    comp_ai_slug: template?.comp_ai_slug || '',
    final_deadline: template?.final_deadline || '',
    compliance_warning: template?.compliance_warning || '',
    active: template?.active !== false,
    downloads_enabled: template?.downloads_enabled !== false,
  });
  const set = (k) => (e) =>
    setValues((v) => ({
      ...v,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));
  return (
    <Modal isOpen onClose={onClose} title={editing ? 'Edit competition' : 'New competition'}>
      <label className={styles.field}>Name
        <input value={values.name} onChange={set('name')} placeholder="IRIS National Fair 2026" />
      </label>
      {!editing && (
        <label className={styles.field}>Slug
          <input value={values.slug} onChange={set('slug')} placeholder="iris-2026" />
          <span className={styles.fieldHint}>Stable id. Cannot change later.</span>
        </label>
      )}
      <label className={styles.field}>Description
        <textarea rows={3} value={values.description} onChange={set('description')} />
      </label>
      <label className={styles.field}>
        Matcher listing slug (optional)
        <input value={values.comp_ai_slug} onChange={set('comp_ai_slug')} placeholder="iris--in" />
        <span className={styles.fieldHint}>
          Links this workspace to the matchable listing so an approved match can open it.
        </span>
      </label>
      <label className={styles.field}>Final deadline
        <input type="date" value={values.final_deadline || ''} onChange={set('final_deadline')} />
      </label>
      <label className={styles.field}>Compliance warning
        <textarea rows={2} value={values.compliance_warning || ''} onChange={set('compliance_warning')} />
        <span className={styles.fieldHint}>Shown as a banner to every student on this competition.</span>
      </label>
      <label className={styles.checkboxField}>
        <input type="checkbox" checked={values.downloads_enabled} onChange={set('downloads_enabled')} />
        Offer generated .docx downloads
      </label>
      <span className={styles.fieldHint}>
        Off for competitions where the only deliverable is a file the student uploads.
      </span>
      {editing && (
        <label className={styles.checkboxField}>
          <input type="checkbox" checked={values.active} onChange={set('active')} />
          Active
        </label>
      )}
      <div className={styles.modalActions}>
        <Button
          onClick={() => onSave(values)}
          disabled={!values.name || (!editing && !values.slug)}
        >
          {editing ? 'Save' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
};

const SectionModal = ({ section, groups, busy, onClose, onSave }) => {
  const [values, setValues] = useState({
    id: section.id,
    req_key: section.req_key || '',
    title: section.title || '',
    instructions: section.instructions || '',
    submission_type: section.submission_type || 'rich_text',
    parent_id: section.parent_id || '',
    min_words: section.min_words || '',
    max_words: section.max_words || '',
    required: section.required !== false,
    // Edited as one-per-line text — a list of short labels is quicker to type
    // and reorder that way than through add/remove rows.
    options: (section.options || []).join('\n'),
    allow_other: !!section.allow_other,
  });
  const set = (k) => (e) =>
    setValues((v) => ({
      ...v,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  const isChoice = values.submission_type === 'select';
  const optionList = values.options.split('\n').map((o) => o.trim()).filter(Boolean);

  const submit = () =>
    onSave({
      ...values,
      parent_id: values.parent_id || null,
      min_words: values.min_words === '' ? null : Number(values.min_words),
      max_words: values.max_words === '' ? null : Number(values.max_words),
      options: isChoice ? optionList : null,
      allow_other: isChoice && values.allow_other,
    });

  return (
    <Modal isOpen onClose={onClose} title={section.id ? 'Edit section' : 'Add section'}>
      <label className={styles.field}>Title
        <input value={values.title} onChange={set('title')} />
      </label>
      <label className={styles.field}>Instructions for the student
        <textarea rows={3} value={values.instructions} onChange={set('instructions')} />
        <span className={styles.fieldHint}>Shown under this section in the student workspace.</span>
      </label>
      {!section.id && (
        <label className={styles.field}>Key
          <input value={values.req_key} onChange={set('req_key')} placeholder="video_script" />
          <span className={styles.fieldHint}>Stable id within this competition. Cannot change later.</span>
        </label>
      )}
      <label className={styles.field}>What the student submits
        <select value={values.submission_type} onChange={set('submission_type')}>
          {SUBMISSION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {section.id && (
          <span className={styles.fieldHint}>
            Cannot change once students have submitted against this section.
          </span>
        )}
      </label>
      <label className={styles.field}>Inside group
        <select value={values.parent_id} onChange={set('parent_id')}>
          <option value="">Top level</option>
          {groups.filter((g) => g.id !== section.id).map((g) => (
            <option key={g.id} value={g.id}>{g.title}</option>
          ))}
        </select>
      </label>
      {isChoice && (
        <>
          <label className={styles.field}>Options
            <textarea
              rows={6}
              value={values.options}
              onChange={set('options')}
              placeholder={'Community Impact\nEducation\nHealth'}
            />
            <span className={styles.fieldHint}>One choice per line, in the order the student sees them.</span>
          </label>
          <label className={styles.checkboxField}>
            <input type="checkbox" checked={values.allow_other} onChange={set('allow_other')} />
            Add an “Other” choice with a free-text box
          </label>
        </>
      )}
      {values.submission_type === 'rich_text' && (
        <div className={styles.fieldRow}>
          <label className={styles.field}>Min words
            <input type="number" value={values.min_words} onChange={set('min_words')} />
          </label>
          <label className={styles.field}>Max words
            <input type="number" value={values.max_words} onChange={set('max_words')} />
          </label>
        </div>
      )}
      <label className={styles.checkboxField}>
        <input type="checkbox" checked={values.required} onChange={set('required')} />
        Required
      </label>
      <div className={styles.modalActions}>
        <Button
          onClick={submit}
          loading={busy}
          disabled={!values.title || (!section.id && !values.req_key) || (isChoice && !optionList.length)}
        >
          {section.id ? 'Save' : 'Add section'}
        </Button>
      </div>
    </Modal>
  );
};

export default CompetitionSetup;
